


import { makeStyles } from 'tss-react/mui';
import { StatusCodes } from 'http-status-codes';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
  MUIDataTable,
  MUIDataTableColumnDef,
  TableActionTypes,
  TableState,
  Typography,
  useDataTable,
} from 'web-library';
import { lastUpdatedInfoModel } from '../../../../api-refactored/exam-configuration/useGetLastUpdatedInfo';
import {
  ExamFeeModelResponse,
  usePostExamFeeListing,
} from '../../../../api-refactored/exam-configuration/usePostExamFeeListing';
import DeleteButton from '../../../../components/buttons/DeleteButton';
import EditButton from '../../../../components/buttons/EditButton';
import DraftDateTimeStamp from '../../../../components/draft-date-time-stamp/DraftDateTimeStamp';
import AdmissionYearAndAddButton from './AdmissionYearAndAddButton';
import AeisExamFeeModal from './AeisExamFeeModal';
import { ExamFeeAccessControl } from './index';
import RemoveModal from './RemoveModal';

const useStyles = makeStyles()((theme) =>
({
  gstInclusive: {
    color: theme.color.text.textTertiary,
  },
  removeButton: {
    [theme.breakpoints.between('md', 'xl')]: {
      paddingLeft: `${theme.spacing(2)} !important`,
    },
    [theme.breakpoints.up('lg')]: {
      paddingLeft: `${theme.spacing(4)} !important`,
    },
  },
}));

export interface SetStateProps {
  setAddModalOpened: Dispatch<React.SetStateAction<boolean>>;
  setDeleteModalOpened: Dispatch<React.SetStateAction<boolean>>;
  setEditState: Dispatch<React.SetStateAction<boolean>>;
  setSelectedExamFee: Dispatch<React.SetStateAction<ExamFeeModelResponse | undefined>>;
}

const getTableColumn = (
  t: TFunction<'translation'>,
  tableState: TableState,
  classes: Record<string,string>,
  accessControl: ExamFeeAccessControl,
  setState: SetStateProps
): MUIDataTableColumnDef[] => {
  const { setAddModalOpened, setDeleteModalOpened, setEditState, setSelectedExamFee } = setState;

  const onEditHandler = (dataIndex: number) => {
    setSelectedExamFee(tableState.data[dataIndex]);
    setEditState(true);
    setAddModalOpened(true);
  };

  const onDeleteHandler = (dataIndex: number) => {
    setSelectedExamFee(tableState.data[dataIndex]);
    setDeleteModalOpened(true);
  };

  return [
    {
      name: 'admissionLevelDescription',
      label: t('exam->>ExamConfiguration.AdmissionLevel'),
    },
    {
      name: 'registrationFee',
      label: t('exam->>ExamFeeConfiguration.RegistrationFee'),
      options: {
        customBodyRenderLite: (dataIndex: number) =>
          `$ ${tableState.data[dataIndex].registrationFee.toFixed(2)}`,
      },
    },
    {
      name: 'lateRegistrationFee',
      label: t('exam->>ExamFeeConfiguration.LateRegistrationFee'),
      options: {
        customBodyRenderLite: (dataIndex: number) =>
          `$ ${tableState.data[dataIndex].lateRegistrationFee.toFixed(2)}`,
      },
    },
    {
      name: 'gst',
      label: t('exam->>ExamFeeConfiguration.Gst'),
      options: {
        customBodyRenderLite: (dataIndex: number) => (
          <>
            <Typography variant="body2">{tableState.data[dataIndex].gstPercent}%</Typography>
            <Typography variant="subtitle2" className={classes.gstInclusive}>
              {tableState.data[dataIndex].gstInclusive
                ? t('exam->>ExamFeeConfiguration.Inclusive')
                : t('exam->>ExamFeeConfiguration.NotInclusive')}
            </Typography>
          </>
        ),
      },
    },
    {
      name: 'actions',
      label: t('exam->>ExamConfiguration.Actions'),
      options: {
        display: accessControl.hasEditAccess || accessControl.hasDeleteAccess,
        customBodyRenderLite: (dataIndex: number) => (
          <>
            {accessControl.hasEditAccess && (
              <EditButton
                buttonId={dataIndex}
                editHandler={() => onEditHandler(dataIndex)}
                label={t('components->>IconLabel.Edit')}
              />
            )}
            {accessControl.hasDeleteAccess && (
              <DeleteButton
                buttonId={dataIndex}
                deleteHandler={() => onDeleteHandler(dataIndex)}
                label={t('components->>IconLabel.Remove')}
                className={classes.removeButton}
              />
            )}
          </>
        ),
      },
    },
  ];
};

interface AeisExamFeeProps {
  selectedExamTypeUuid: string;
  setNoAccess: Dispatch<SetStateAction<boolean>>;
  accessControl: ExamFeeAccessControl;
}

const { FORBIDDEN } = StatusCodes;

const AeisExamFee = ({ selectedExamTypeUuid, setNoAccess, accessControl }: AeisExamFeeProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [lastUpdatedInfo, setLastUpdatedInfo] = useState<lastUpdatedInfoModel>({
    lastUpdatedTime: null,
    name: null,
  });
  const { state: tableState, dispatch: tableDispatch } = useDataTable();
  const [isAddModalOpened, setAddModalOpened] = useState<boolean>(false);
  const [isDeleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);
  const [isEditState, setEditState] = useState<boolean>(false);
  const [selectedExamFee, setSelectedExamFee] = useState<ExamFeeModelResponse>();

  const {
    isLoading: isExamFeeListingLoading,
    mutate: examFeeListingMutate,
  } = usePostExamFeeListing({
    onSuccess: (data) => {
      if (data) {
        tableDispatch({
          type: 'UPDATE_DATA',
          payload: {
            data: data.data,
            count: data.data.length,
          },
        } as TableActionTypes);
        setLastUpdatedInfo({
          lastUpdatedTime: data.lastUpdatedAt,
          name: data.lastUpdatedBy,
        });
      }
    },
    onError: (err) => {
      if (err.response?.status === FORBIDDEN) {
        setNoAccess(true);
      }
    },
  });

  const fetchExamFeeListing = () => {
    if (selectedYear) {
      examFeeListingMutate({
        registrationExamLevelUuid: selectedExamTypeUuid,
        year: selectedYear,
      });
    }
  };

  useEffect(() => {
    fetchExamFeeListing();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [selectedYear]);

  const onAddHandler = () => {
    setSelectedExamFee(undefined);
    setEditState(false);
    setAddModalOpened(true);
  };

  return (
    <>
      <AdmissionYearAndAddButton
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        addButtonHandler={onAddHandler}
        setNoAccess={setNoAccess}
        hasCreateAuthority={accessControl.hasCreateAccess}
      />

      <MUIDataTable
        key="aeis-exam-fee-listing"
        isLoading={isExamFeeListingLoading}
        columns={getTableColumn(t, tableState, classes, accessControl, {
          setAddModalOpened,
          setDeleteModalOpened,
          setEditState,
          setSelectedExamFee,
        })}
        data={tableState.data}
        dispatch={tableDispatch}
        state={tableState}
        emptyDataLabel={t('exam->>ExamFeeConfiguration.NoExamFeeAdded')}
      />

      <DraftDateTimeStamp lastUpdatedInfo={lastUpdatedInfo} />

      {selectedYear && (
        <AeisExamFeeModal
          isEditState={isEditState}
          isModalOpened={isAddModalOpened}
          setModalOpened={setAddModalOpened}
          defaultData={selectedExamFee}
          registrationExamLevelUuid={selectedExamTypeUuid}
          year={selectedYear}
          fetchExamFeeListing={fetchExamFeeListing}
        />
      )}
      <RemoveModal
        isModalOpened={isDeleteModalOpened}
        setModalOpened={setDeleteModalOpened}
        fetchExamFeeListing={fetchExamFeeListing}
        defaultData={selectedExamFee}
        isAeisExam
      />
    </>
  );
};

export default AeisExamFee;
